import {
  siApache,
  siGooglecloud,
  siHubspot,
  siJira,
  siMongodb,
  siMysql,
  siPostgresql,
  siSap,
  siShopify,
  siSnowflake,
  siStripe,
  siZendesk,
} from "simple-icons";
import { Plug } from "lucide-react";
import { cn } from "../../lib/cn";

/** Vector logos for the systems we ship connectors for.
 *
 * simple-icons is already bundled, so a listing stores only a slug and we
 * resolve it here — no image uploads, no external requests (which the
 * artifact CSP would block anyway). Brands simple-icons doesn't carry, or
 * whose marks are trademark-restricted, fall back to a coloured initial.
 */
const ICONS: Record<string, { path: string; hex: string }> = {
  hubspot: { path: siHubspot.path, hex: siHubspot.hex },
  jira: { path: siJira.path, hex: siJira.hex },
  mongodb: { path: siMongodb.path, hex: siMongodb.hex },
  mysql: { path: siMysql.path, hex: siMysql.hex },
  postgresql: { path: siPostgresql.path, hex: siPostgresql.hex },
  postgres: { path: siPostgresql.path, hex: siPostgresql.hex },
  sap: { path: siSap.path, hex: siSap.hex },
  shopify: { path: siShopify.path, hex: siShopify.hex },
  snowflake: { path: siSnowflake.path, hex: siSnowflake.hex },
  stripe: { path: siStripe.path, hex: siStripe.hex },
  zendesk: { path: siZendesk.path, hex: siZendesk.hex },
  apache: { path: siApache.path, hex: siApache.hex },
  googlecloud: { path: siGooglecloud.path, hex: siGooglecloud.hex },
};

/** Brands simple-icons dropped for trademark reasons (Salesforce, Slack,
 *  ServiceNow, Oracle …) — shown as a monogram in the brand colour. */
const FALLBACK_COLORS: Record<string, string> = {
  salesforce: "00A1E0",
  slack: "611F69",
  servicenow: "62D84E",
  netsuite: "1F5788",
  oracle: "C74634",
  workday: "F68D2E",
  sharepoint: "0078D4",
  dynamics: "002050",
  rest: "6366F1",
  http: "6366F1",
  api: "6366F1",
};

export function resolveLogo(iconSlug?: string, systemType?: string) {
  const key = (iconSlug || systemType || "").trim().toLowerCase();
  return { key, icon: ICONS[key] ?? null, fallbackHex: FALLBACK_COLORS[key] ?? null };
}

export function SystemLogo({
  iconSlug,
  systemType,
  brandColor,
  name,
  className,
  size = 24,
}: {
  iconSlug?: string;
  systemType?: string;
  brandColor?: string;
  name?: string;
  className?: string;
  size?: number;
}) {
  const { icon, fallbackHex } = resolveLogo(iconSlug, systemType);
  const hex = (brandColor || icon?.hex || fallbackHex || "").replace(/^#/, "");
  const color = hex ? `#${hex}` : "currentColor";

  if (icon) {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        className={cn("shrink-0", className)}
        role="img"
        aria-label={name ? `${name} logo` : undefined}
        aria-hidden={name ? undefined : true}
      >
        <path d={icon.path} fill={color} />
      </svg>
    );
  }

  // No vector mark: a monogram in the brand colour still reads as that system.
  const letter = (name || systemType || "?").trim().charAt(0).toUpperCase();
  if (hex) {
    return (
      <span
        className={cn(
          "flex shrink-0 items-center justify-center rounded-md font-bold text-white",
          className,
        )}
        style={{ backgroundColor: color, width: size, height: size, fontSize: size * 0.55 }}
        aria-label={name ? `${name} logo` : undefined}
      >
        {letter}
      </span>
    );
  }
  return <Plug className={cn("shrink-0", className)} style={{ width: size, height: size }} />;
}
